import { LogLevel } from "@azure/msal-browser";
import sharedConfig from "../../shared-config.json";

// B2C Configuration (for participants) - imported from shared config
export const b2cConfig = {
  tenantName: sharedConfig.azure.b2c.tenantName,
  clientId: sharedConfig.azure.b2c.clientId,
  // Basic sign-in flow names
  signUpSignInFlowName: sharedConfig.userFlows.signUpSignIn,
  signInEmailFlowName: sharedConfig.userFlows.signInEmail,
  signInPhoneFlowName: sharedConfig.userFlows.signInPhone,
  signInMicrosoftFlowName: sharedConfig.userFlows.signInMicrosoft,
  signInGoogleFlowName: sharedConfig.userFlows.signInGoogle,
  // Signup flow names
  signUpFlowName: sharedConfig.userFlows.signUpEmail,
  signUpEmailFlowName: sharedConfig.userFlows.signUpEmail,
  signUpPhoneFlowName: sharedConfig.userFlows.signUpPhone,
  signUpMicrosoftFlowName: sharedConfig.userFlows.signUpMicrosoft,
  signUpGoogleFlowName: sharedConfig.userFlows.signUpGoogle,
  // Password reset flow
  forgotPasswordFlowName: sharedConfig.userFlows.passwordReset,
};

// Helper to get B2C authority URL for a specific user flow
const getB2CAuthority = (userFlow) =>
  `https://${b2cConfig.tenantName}.b2clogin.com/${b2cConfig.tenantName}.onmicrosoft.com/${userFlow}`;

// MSAL config for B2C (participants)
export const b2cMsalConfig = {
  auth: {
    clientId: b2cConfig.clientId,
    authority: getB2CAuthority(b2cConfig.signUpSignInFlowName),
    knownAuthorities: [`${b2cConfig.tenantName}.b2clogin.com`],
    // Dynamically set redirectUri based on current origin
    redirectUri:
      typeof window !== "undefined"
        ? `${window.location.origin}/participant`
        : process.env.NODE_ENV === "production"
        ? process.env.REACT_APP_BASE_URL
          ? `${process.env.REACT_APP_BASE_URL}/participant`
          : `${sharedConfig.urls.pilot.web}/participant` // Use pilot URL as default for initial deployment
        : `${sharedConfig.urls.development.web}/participant`,
    navigateToLoginRequestUrl: false,
    usePkce: true,
  },
  cache: {
    cacheLocation: "localStorage", // B2C users (participants) use localStorage for persistent login across browser sessions
    storeAuthStateInCookie: false, // No need to store in cookies as modern browsers fully support localStorage
  },
  system: {
    allowNativeBroker: false,
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
    },
  },
};

// MSAL config for Entra ID (admin/PI) - imported from shared config
export const msalConfig = {
  auth: {
    clientId: sharedConfig.azure.entra.clientId,
    authority: `https://login.microsoftonline.com/${sharedConfig.azure.entra.tenantId}`,
    // Dynamically set redirectUri based on current origin
    redirectUri:
      typeof window !== "undefined"
        ? `${window.location.origin}/admin`
        : process.env.NODE_ENV === "production"
        ? process.env.REACT_APP_BASE_URL
          ? `${process.env.REACT_APP_BASE_URL}/admin`
          : `${sharedConfig.urls.pilot.web}/admin` // Use pilot URL as default for initial deployment
        : `${sharedConfig.urls.development.web}/admin`,
    usePkce: true,
  },
  cache: {
    cacheLocation: "sessionStorage", // Entra ID users (admins/PIs) use sessionStorage for session-only login that clears on browser close
    storeAuthStateInCookie: false, // No need to store in cookies as modern browsers fully support sessionStorage
  },
  system: {
    allowNativeBroker: false,
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
    },
  },
};

// Define API scopes for the Azure Function app - imported from shared config
export const apiScopes = {
  // Use the exact audience format expected by the server for B2C
  participantScope: sharedConfig.scopes.participant.scope,

  // For Entra ID, use the URI format required for the API audience validation
  adminScope: sharedConfig.scopes.admin.scope,

  // Include documentation about why these formats are crucial
  // The audience value in the resulting token MUST match what the server expects:
  // - For B2C: The audience is set to the actual application ID
  // - For Entra ID: The audience is set to the API URI identifier

  // Debug info - for troubleshooting
  debugInfo: () => {
    console.group(`${sharedConfig.project.name} API Scope Debugging`);
    console.log("Current participantScope:", apiScopes.participantScope);
    console.log("Current adminScope:", apiScopes.adminScope);
    console.log("B2C Client ID:", b2cConfig.clientId);
    console.log("B2C Tenant:", b2cConfig.tenantName);
    console.log(
      "B2C Authority:",
      getB2CAuthority(b2cConfig.signUpSignInFlowName)
    );
    console.groupEnd();
  },
};

export const b2cLoginRequest = {
  authority: getB2CAuthority(b2cConfig.signInEmailFlowName),
  scopes: ["openid", "offline_access", apiScopes.participantScope],
  prompt: "login",
  state: "b2c",
  extraQueryParameters: {
    response_mode: "fragment",
    p: "B2C_1_signin_email",
  },
};

// Registration request for B2C with Email only
export const b2cEmailRegistrationRequest = {
  authority: getB2CAuthority(b2cConfig.signUpEmailFlowName),
  scopes: ["openid", "offline_access", apiScopes.participantScope],
  state: "b2c_email_registration",
  extraQueryParameters: {
    response_mode: "fragment",
    p: "B2C_1_signup_email",
  },
};

// Registration request for B2C with Microsoft
export const b2cMicrosoftRegistrationRequest = {
  authority: getB2CAuthority(b2cConfig.signUpMicrosoftFlowName),
  scopes: ["openid", "offline_access", apiScopes.participantScope],
  state: "b2c_microsoft_registration",
  extraQueryParameters: {
    p: "B2C_1_signup_microsoft",
  },
};

// Registration request for B2C with Google
export const b2cGoogleRegistrationRequest = {
  authority: getB2CAuthority(b2cConfig.signUpGoogleFlowName),
  scopes: ["openid", "offline_access", apiScopes.participantScope],
  state: "b2c_google_registration",
  extraQueryParameters: {
    response_mode: "fragment",
    p: "B2C_1_signup_google",
  },
};

// Email login request
export const b2cEmailLoginRequest = {
  authority: getB2CAuthority(b2cConfig.signInEmailFlowName),
  scopes: ["openid", "offline_access", apiScopes.participantScope],
  prompt: "login",
  state: "b2c_email",
  extraQueryParameters: {
    response_mode: "fragment",
    p: "B2C_1_signin_email",
  },
};

// Phone login request
export const b2cPhoneLoginRequest = {
  authority: getB2CAuthority(b2cConfig.signInPhoneFlowName),
  scopes: ["openid", "offline_access", apiScopes.participantScope],
  prompt: "login",
  state: "b2c_phone",
  extraQueryParameters: {
    response_mode: "fragment",
    p: "B2C_1_signin_phone",
  },
};

// Login request for B2C with Microsoft
export const b2cMicrosoftLoginRequest = {
  authority: getB2CAuthority(b2cConfig.signInMicrosoftFlowName),
  scopes: ["openid", "offline_access", apiScopes.participantScope],
  prompt: "select_account",
  state: "b2c_microsoft",
  extraQueryParameters: {
    response_mode: "fragment",
    p: "B2C_1_signin_microsoft",
  },
};

// Login request for B2C with Google
export const b2cGoogleLoginRequest = {
  authority: getB2CAuthority(b2cConfig.signInGoogleFlowName),
  scopes: ["openid", "offline_access", apiScopes.participantScope],
  prompt: "select_account",
  state: "b2c_google",
  extraQueryParameters: {
    response_mode: "fragment",
    domain_hint: "google.com",
    p: "B2C_1_signin_google",
  },
};

// Login request for Entra ID
export const loginRequest = {
  scopes: ["openid", "offline_access", apiScopes.adminScope],
  prompt: "select_account",
  state: "entra",
  extraQueryParameters: {
    response_mode: "fragment",
  },
};

// Password reset config for B2C
export const b2cPasswordResetRequest = {
  authority: getB2CAuthority(b2cConfig.forgotPasswordFlowName),
  scopes: ["openid", "offline_access", apiScopes.participantScope],
  state: "b2c_password_reset",
};

// Registration request for B2C with Phone
export const b2cPhoneRegistrationRequest = {
  authority: getB2CAuthority(b2cConfig.signUpPhoneFlowName),
  scopes: ["openid", "offline_access", apiScopes.participantScope],
  state: "b2c_phone_registration",
  extraQueryParameters: {
    p: "B2C_1_signup_phone",
    response_mode: "fragment",
  },
};

// Graph API config (for admin/PI operations)
export const graphConfig = {
  graphMeEndpoint: "https://graph.microsoft.com/v1.0/me",
};
